import classNames from 'classnames';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../../store/types';
import { AgendaLayoutTypesClassic, TemplateClassNames } from '../../../../types/template-layouts';
import {
	BrandliveEvent,
	ModuleMap,
	PageModule,
	PageModuleType,
	RegistrationStepType,
	Session,
	SessionPreview,
	SessionTypesEnum,
} from '../../../../types/working-model';
import TabHeader from '../../../general-ui/tab-header/tab-header';
import { Button } from '../../../general-ui/button/button';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import { eventPath, getStylingOverrides } from '../../utils';
import SessionCard from './agenda-session-card';
import { BroadcastSessions } from './broadcast-sessions';
import { OnDemandSessions } from './ondemand-sessions';

import './agenda.scss';
import { getDefaultAgendaImage } from '../../../../store/utils/create-event';
import ReactMarkdown from 'react-markdown';
import { getTextValue } from '../../../../utils/utils';
import { useTranslation } from 'react-i18next';
interface AgendaProps {
	module: PageModule;
	template: TemplateClassNames;
	sessions: Session[] | SessionPreview[];
	isLive: boolean;
	isUpNext?: boolean;
	eventBundle: BrandliveEvent | null;
}

// TODO uncomment when TAB style will ready
// const BROADCAST = `Broadcast sessions`;
// const ON_DEMAND = `On demand sessions`;

const Agenda = React.forwardRef<HTMLDivElement, AgendaProps>(({ module, template, sessions, isLive, isUpNext, eventBundle }, ref) => {
	const params: ParamsProps = useParams();
	const history = useHistory();
	const language = params?.language || 'en';
	const { content, styling_overrides } = module;
	const layout_type = content?.layout_type || AgendaLayoutTypesClassic.standard;
	const { t } = useTranslation('homepage');
	const BROADCAST = t(`Broadcast sessions`);
	const ON_DEMAND = t(`On demand sessions`);
	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
		titleOverrides,
		descriptionOverrides,
		customHeadingOverrides,
	} = stylingOverrides;

	const ticketingStep = eventBundle!.registration_steps?.find(step => step.type == RegistrationStepType.ticketing && step.isOn);
	const channel = eventBundle!.channel;

	const {
		LiveEventReducer: {
			blProfileUser,
		},
	} = useSelector((event: EventsState) => event);

	const filterSessions = useCallback((sessions: (Session | SessionPreview)[], sessionType: string): any[] => {
		const available = sessions.filter((session: Session | SessionPreview) => {
			if (session.session_type !== sessionType) {
				return false;
			}

			const ticket = blProfileUser?.profile?.[channel].ticket;
			const ticketData = ticketingStep?.ticketing?.find(_ticket => _ticket.uuid == ticket);

			if (!ticket || !ticketData) {
				return true;
			}

			let sessionBelongsToATicket = false;
			const foundSession = ticketingStep?.ticketing?.find(ticket => ticket.session_uuids.includes(session.uuid));
			if (foundSession) {
				sessionBelongsToATicket = true;
			}

			if (ticketingStep?.ticketing) {
				for (const ticket of ticketingStep.ticketing) {
					if (ticket.session_uuids.indexOf(session.uuid) !== -1) {
						sessionBelongsToATicket = true;
						break;
					}
				}
			}

			return ticketData?.session_uuids.indexOf(session.uuid) !== -1 || !sessionBelongsToATicket;
		});

		return available;
	}, [blProfileUser?.profile, channel, ticketingStep?.ticketing]);

	const broadcastSessionList = useMemo(
		() => filterSessions(sessions, SessionTypesEnum.broadcast),
		[sessions, filterSessions]
	);
	const onDemandSessionList = useMemo(
		() => filterSessions(sessions, SessionTypesEnum.onDemand),
		[sessions, filterSessions]
	);

	useEffect(() => {
		// Fix bug with not updated underline
		const interval = setTimeout(() => {
			setActiveTab(BROADCAST);
		});
		return () => clearTimeout(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const useTabs = broadcastSessionList.length && onDemandSessionList.length;

	const [activeTab, setActiveTab] = useState<string>();
	const renderSessions = (): JSX.Element | undefined => {
		if (isLive) {
			//can't use the && syntax here because it's rendering the string "0" in the DOM. 
			return (
				<>
					{useTabs ? (
						<TabHeader
							tabs={[BROADCAST, ON_DEMAND]}
							onClick={setActiveTab}
							currentTab={activeTab}
							template={template}
						/>
					) : null}

					{((useTabs && activeTab === BROADCAST && broadcastSessionList.length) || (!useTabs && broadcastSessionList.length)) ? (
						<BroadcastSessions
							module={module}
							sessions={broadcastSessionList}
							template={template}
							isLive={isLive}
							isUpNext={isUpNext}
						/>
					) : null}

					{((activeTab === ON_DEMAND && onDemandSessionList.length) || (!useTabs && onDemandSessionList.length)) ? (
						<OnDemandSessions
							module={module}
							sessions={onDemandSessionList}
							template={template}
							isLive={isLive}
						/>
					) : null}
				</>
			);
		} else {
			return <>
				{broadcastSessionList.length ? <BroadcastSessions
					module={module}
					sessions={broadcastSessionList}
					template={template}
					isLive={isLive}
					isUpNext={isUpNext}
				/> : null}
				{onDemandSessionList.length ? <OnDemandSessions
					module={module}
					sessions={onDemandSessionList}
					template={template}
					isLive={isLive}
				/> : null}
			</>;
		}
	};

	if (!sessions?.length || !content) return null;
	const { custom_heading, title, description } = getTextValue(content, language);
	return (
		<section
			id={ModuleMap[PageModuleType.agenda]}
			className={classNames(
				'agenda',
				`agenda-${layout_type}`,
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				{
					!isUpNext ?
						<>
							<TypographyItem className={classNames('evt-caption-1 upper-agenda-title', customHeadingOverrides)} tagName="p">
								{custom_heading}
							</TypographyItem>
							<TypographyItem className={classNames('evt-heading-2 agenda-title', titleOverrides)} tagName="h2">
								{title}
							</TypographyItem>
							{/*
								the next TypographyItem originally had tagName="p", however ReactMarkdown also has a p tag
								which causes invalid nesting of p tags. Removing it seemed to cause no style regressions
							*/}
							<TypographyItem className={classNames('evt-body-text-1 agenda-description', descriptionOverrides)}>
								<ReactMarkdown source={description} />
							</TypographyItem>
						</> :
						null
				}

				<div className={classNames('agenda-featured-container')}>
					{/* TODO: for example, I brought out a default card */}
					{
						sessions && sessions[0] && <SessionCard
							key={sessions[0].uuid}
							session={sessions[0]}
							stylingOverrides={stylingOverrides}
							template={template}
							defaultImage={getDefaultAgendaImage(template, 0)}
						/>
					}
				</div>
				<div className={classNames('agenda-sessions-container')}>
					{
						isUpNext &&
						<div className="agenda-next-sessions-header">
							<TypographyItem className={classNames('evt-heading-2 agenda-title')} tagName="h2">
								{t("Up next")}
							</TypographyItem>
							<Button
								tagName="button"
								onClick={() => history.push(`/${eventPath(params.eventName, params.uuid)}/${language}/sessions`)}
								classButton="tertiary"
								template={template}
							>
								{t("See agenda")}
							</Button>
						</div>
					}
					{renderSessions()}
				</div>
			</div>
		</section>
	);
});

Agenda.displayName = 'Agenda';

export default Agenda;
